/**
 * GreenStayView Component
 * Phase P2.2-10: Full-page Green Stay management with Save/Discard UX
 *
 * Dedicated concierge view for managing all Green Stay preferences.
 * All changes are pending until explicitly saved. Prevents activity feed spam.
 */

import { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import { Sparkles, Droplets, Wind, Zap, Check, Save, RotateCcw, X, AlertTriangle } from 'lucide-react';
import { calculateStaySavings } from '@/services/greenStay';
import {
  logGreenStayUpdate
} from '@/services/ecoLogger';

interface GreenStayViewProps {
  greenStayEnabled: boolean;
  skipCleaningDays: string[];
  towelsOnlyDays: string[];
  stayCheckIn: string;
  stayCheckOut: string;
  roomNumber: string;
  stayId: string;
  onGreenStayEnable: () => void;
  onGreenStayDisable: () => void;
  onSkipCleaningChange: (days: string[], opts?: { silent?: boolean }) => void;
  onTowelsOnlyChange: (days: string[], opts?: { silent?: boolean }) => void;
  onBack: () => void;
  onVoiceCommandReady?: (handler: (command: GreenStayVoiceCommand, date?: string) => string) => void;
  isActive?: boolean;
}

type GreenStayVoiceCommand =
  | 'ENABLE_ECO'
  | 'DISABLE_ECO'
  | 'SKIP_CLEANING_DATE'
  | 'TOWELS_ONLY_DATE'
  | 'RESET_PREFERENCES'
  | 'SAVE_CHANGES'
  | 'DISCARD_CHANGES'
  | 'EXIT_ATTEMPT';

export type { GreenStayVoiceCommand };

// Helper to check if date is in the past
function isPastDate(dateStr: string): boolean {
  const date = new Date(dateStr + 'T00:00:00Z');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
}

// Helper to format date for voice feedback (e.g. "tomorrow" or "Monday")
function formatDayLabel(dateStr: string): string {
  const date = new Date(dateStr + 'T12:00:00');
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (dateStr === today.toISOString().split('T')[0]) return 'today';
  if (dateStr === tomorrow.toISOString().split('T')[0]) return 'tomorrow';
  return date.toLocaleDateString('en-US', { weekday: 'long' });
}

// Helper to get date range
function getDateRange(startDate: string, endDate: string): Array<{ date: string; isPast: boolean }> {
  // Extract YYYY-MM-DD from ISO timestamp if present
  const startDateStr = startDate.split('T')[0];
  const endDateStr = endDate.split('T')[0];

  const start = new Date(startDateStr + 'T00:00:00Z');
  const end = new Date(endDateStr + 'T00:00:00Z');
  const dates: Array<{ date: string; isPast: boolean }> = [];

  const current = new Date(start);
  while (current < end) {
    const dateStr = current.toISOString().split('T')[0];
    if (dateStr) {
      dates.push({
        date: dateStr,
        isPast: isPastDate(dateStr)
      });
    }
    current.setUTCDate(current.getUTCDate() + 1);
  }

  return dates;
}

// Helper to get day of week label
function getDayOfWeek(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00Z');
  const days = ['Su', 'M', 'T', 'W', 'Th', 'F', 'Sa'];
  return days[date.getUTCDay()] || 'Su';
}

// Helper to calculate impact score (0-100)
// Based on: % of stay days with eco choices + weighted by service mode
function calculateImpactScore(
  skipCleaningDays: string[],
  towelsOnlyDays: string[],
  stayCheckIn: string,
  stayCheckOut: string
): number {
  // Get total stay days using same logic as getDateRange (check-in inclusive, check-out exclusive)
  const dateRange = getDateRange(stayCheckIn, stayCheckOut);
  const totalDays = Math.max(1, dateRange.length);

  // Count eco days with weights:
  // Skip cleaning (NO_SERVICE) = 1.0 point per day (full impact)
  // Towels only (TOWELS_ONLY) = 0.6 point per day (partial impact)
  const skipDays = skipCleaningDays.length * 1.0;
  const towelDays = towelsOnlyDays.filter(d => !skipCleaningDays.includes(d)).length * 0.6;
  const totalEcoPoints = skipDays + towelDays;

  // Calculate score: (eco points / total days) * 100
  // This gives a meaningful percentage of eco-participation
  const score = Math.round((totalEcoPoints / totalDays) * 100);

  // Cap at 100 (if somehow eco days exceed stay length due to overlaps)
  return Math.min(100, score);
}

function GreenStayView({
  greenStayEnabled,
  skipCleaningDays,
  towelsOnlyDays,
  stayCheckIn,
  stayCheckOut,
  roomNumber,
  stayId,
  onGreenStayEnable,
  onGreenStayDisable,
  onSkipCleaningChange,
  onTowelsOnlyChange,
  onBack,
  onVoiceCommandReady,
  isActive = true,
}: GreenStayViewProps) {
  // Track stay dates
  const stayDates = useMemo(() => getDateRange(stayCheckIn, stayCheckOut), [stayCheckIn, stayCheckOut]);
  const futureDates = useMemo(() => stayDates.filter(d => !d.isPast), [stayDates]);

  // Pending state (not saved until user clicks "Save")
  const [pendingEcoEnabled, setPendingEcoEnabled] = useState(greenStayEnabled);
  const [pendingSkipDays, setPendingSkipDays] = useState<string[]>(skipCleaningDays);
  const [pendingTowelsDays, setPendingTowelsDays] = useState<string[]>(towelsOnlyDays);

  // Exit warning modal
  const [showExitWarning, setShowExitWarning] = useState(false);
  const [modalFocusedButton, setModalFocusedButton] = useState<'save' | 'discard'>('save');

  // Track if user confirmed exit (to prevent double-showing modal)
  const isExitingRef = useRef(false);

  // Sync pending state when props change (after save)
  useEffect(() => {
    setPendingEcoEnabled(greenStayEnabled);
  }, [greenStayEnabled]);

  useEffect(() => {
    setPendingSkipDays(skipCleaningDays);
  }, [skipCleaningDays]);

  useEffect(() => {
    setPendingTowelsDays(towelsOnlyDays);
  }, [towelsOnlyDays]);

  // Keyboard navigation state
  const [focusedSection, setFocusedSection] = useState<'eco' | 'skip' | 'towels' | 'save' | 'discard'>('eco');
  const [focusedDateIndex, setFocusedDateIndex] = useState(0);

  // Check if there are unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    if (pendingEcoEnabled !== greenStayEnabled) return true;
    if (JSON.stringify([...pendingSkipDays].sort()) !== JSON.stringify([...skipCleaningDays].sort())) return true;
    if (JSON.stringify([...pendingTowelsDays].sort()) !== JSON.stringify([...towelsOnlyDays].sort())) return true;
    return false;
  }, [pendingEcoEnabled, pendingSkipDays, pendingTowelsDays, greenStayEnabled, skipCleaningDays, towelsOnlyDays]);

  // Calculate savings based on pending state
  const savingsInfo = useMemo(() =>
    calculateStaySavings(pendingSkipDays, pendingTowelsDays),
    [pendingSkipDays, pendingTowelsDays]
  );

  // Toggle handlers for pending state
  const toggleEcoMode = useCallback(() => {
    setPendingEcoEnabled(prev => {
      const newValue = !prev;
      // When disabling eco mode, clear future dates but keep past ones
      if (!newValue) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        setPendingSkipDays(current => current.filter(date => {
          const d = new Date(date + 'T00:00:00Z');
          return d < today;
        }));

        setPendingTowelsDays(current => current.filter(date => {
          const d = new Date(date + 'T00:00:00Z');
          return d < today;
        }));
      }
      return newValue;
    });
  }, []);

  const toggleSkipCleaningDay = useCallback((date: string, isPast: boolean) => {
    if (isPast || !pendingEcoEnabled) return;
    const isAdding = !pendingSkipDays.includes(date);
    setPendingSkipDays(prev =>
      isAdding ? [...prev, date] : prev.filter(d => d !== date)
    );
    // Mutually exclusive: deselect towels-only for this day when skip cleaning is applied
    if (isAdding) {
      setPendingTowelsDays(prev => prev.filter(d => d !== date));
    }
  }, [pendingEcoEnabled, pendingSkipDays]);

  const toggleTowelsOnlyDay = useCallback((date: string, isPast: boolean) => {
    if (isPast || !pendingEcoEnabled) return;
    const isAdding = !pendingTowelsDays.includes(date);
    setPendingTowelsDays(prev =>
      isAdding ? [...prev, date] : prev.filter(d => d !== date)
    );
    // Mutually exclusive: deselect skip-cleaning for this day when towels-only is applied
    if (isAdding) {
      setPendingSkipDays(prev => prev.filter(d => d !== date));
    }
  }, [pendingEcoEnabled, pendingTowelsDays]);

  const setAllSkipCleaning = useCallback(() => {
    if (!pendingEcoEnabled) return;
    setPendingSkipDays(futureDates.map(d => d.date));
  }, [pendingEcoEnabled, futureDates]);

  const clearAllSkipCleaning = useCallback(() => {
    if (!pendingEcoEnabled) return;
    setPendingSkipDays([]);
  }, [pendingEcoEnabled]);

  const setAllTowelsOnly = useCallback(() => {
    if (!pendingEcoEnabled) return;
    const futureDateStrings = futureDates.map(d => d.date);
    setPendingTowelsDays(futureDateStrings);
    // Mutually exclusive: clear these days from skip cleaning
    setPendingSkipDays(prev => prev.filter(d => !futureDateStrings.includes(d)));
  }, [pendingEcoEnabled, futureDates]);

  const clearAllTowelsOnly = useCallback(() => {
    if (!pendingEcoEnabled) return;
    setPendingTowelsDays([]);
  }, [pendingEcoEnabled]);

  // Save handler - applies all pending changes and logs a single consolidated eco event
  const handleSave = useCallback(() => {
    const hasChanges = pendingEcoEnabled !== greenStayEnabled ||
                       JSON.stringify([...pendingSkipDays].sort()) !== JSON.stringify([...skipCleaningDays].sort()) ||
                       JSON.stringify([...pendingTowelsDays].sort()) !== JSON.stringify([...towelsOnlyDays].sort());

    if (!hasChanges) return;

    // Apply eco mode change
    if (pendingEcoEnabled !== greenStayEnabled) {
      if (pendingEcoEnabled) {
        onGreenStayEnable();
      } else {
        onGreenStayDisable();
      }
    }

    // Apply date changes to parent state (silent: suppress App.tsx toast/savings overlay)
    onSkipCleaningChange(pendingSkipDays, { silent: true });
    onTowelsOnlyChange(pendingTowelsDays, { silent: true });

    // Log a single consolidated update event
    logGreenStayUpdate(roomNumber, stayId, {
      ecoModeChanged: pendingEcoEnabled !== greenStayEnabled ? { from: greenStayEnabled, to: pendingEcoEnabled } : undefined,
      skipCleaningDates: pendingSkipDays,
      towelsOnlyDates: pendingTowelsDays
    });

    // Note: No need to reset pending state - useEffect will sync it
  }, [
    pendingEcoEnabled, pendingSkipDays, pendingTowelsDays,
    greenStayEnabled, skipCleaningDays, towelsOnlyDays,
    onGreenStayEnable, onGreenStayDisable,
    onSkipCleaningChange, onTowelsOnlyChange,
    roomNumber, stayId
  ]);

  // Discard handler - revert all pending changes
  const handleDiscard = useCallback(() => {
    setPendingEcoEnabled(greenStayEnabled);
    setPendingSkipDays(skipCleaningDays);
    setPendingTowelsDays(towelsOnlyDays);
    setShowExitWarning(false);
  }, [greenStayEnabled, skipCleaningDays, towelsOnlyDays]);

  // Exit attempt handler - show warning if unsaved changes
  const handleExitAttempt = useCallback(() => {
    console.log('[GreenStayView] ========== handleExitAttempt called ==========');
    console.log('[GreenStayView] hasUnsavedChanges:', hasUnsavedChanges);

    if (hasUnsavedChanges && !isExitingRef.current) {
      console.log('[GreenStayView] SETTING showExitWarning to TRUE');
      setShowExitWarning(true);
      return false; // Block exit
    } else {
      console.log('[GreenStayView] No changes or already confirmed, calling onBack');
      isExitingRef.current = true;
      onBack();
      return true; // Allow exit
    }
  }, [hasUnsavedChanges, onBack]);

  // Exit with save
  const handleSaveAndExit = useCallback(() => {
    handleSave();
    setShowExitWarning(false);
    isExitingRef.current = true;
    onBack();
  }, [handleSave, onBack]);

  // Exit without save
  const handleDiscardAndExit = useCallback(() => {
    handleDiscard();
    setShowExitWarning(false);
    isExitingRef.current = true;
    onBack();
  }, [handleDiscard, onBack]);

  // Voice command handler
  const handleVoiceCommand = useCallback((command: GreenStayVoiceCommand, date?: string): string => {
    switch (command) {
      case 'SKIP_CLEANING_DATE': {
        if (!date) return "I couldn't understand which day you mean.";
        if (!pendingEcoEnabled) {
          setPendingEcoEnabled(true);
        }
        if (pendingSkipDays.includes(date)) {
          const dayLabel = formatDayLabel(date);
          return `Skip cleaning is already set for ${dayLabel}. Remember to save your changes.`;
        }
        setPendingSkipDays(prev => [...prev, date]);
        const dayLabel = formatDayLabel(date);
        return `Skip cleaning added for ${dayLabel}. Remember to save your changes.`;
      }

      case 'TOWELS_ONLY_DATE': {
        if (!date) return "I couldn't understand which day you mean.";
        if (!pendingEcoEnabled) {
          setPendingEcoEnabled(true);
        }
        const isRemoving = pendingTowelsDays.includes(date);
        if (isRemoving) {
          setPendingTowelsDays(prev => prev.filter(d => d !== date));
          setPendingSkipDays(prev => prev.filter(d => d !== date));
          const dayLabel = formatDayLabel(date);
          return `Towels only removed for ${dayLabel}. Remember to save your changes.`;
        } else {
          setPendingTowelsDays(prev => [...prev, date]);
          setPendingSkipDays(prev => prev.includes(date) ? prev : [...prev, date]);
          const dayLabel = formatDayLabel(date);
          return `Towels only added for ${dayLabel}. Remember to save your changes.`;
        }
      }

      case 'ENABLE_ECO':
        if (!pendingEcoEnabled) {
          setPendingEcoEnabled(true);
          return "Green Stay mode enabled. Remember to save your changes.";
        }
        return "Green Stay mode is already enabled.";

      case 'DISABLE_ECO':
        if (pendingEcoEnabled) {
          setPendingEcoEnabled(false);
          return "Green Stay mode disabled. Remember to save your changes.";
        }
        return "Green Stay mode is already disabled.";

      case 'SAVE_CHANGES':
        if (hasUnsavedChanges) {
          handleSave();
          return "Your Green Stay preferences have been saved.";
        }
        return "No changes to save.";

      case 'DISCARD_CHANGES':
        if (hasUnsavedChanges) {
          handleDiscard();
          return "Changes discarded.";
        }
        return "No changes to discard.";

      case 'RESET_PREFERENCES':
        setPendingSkipDays([]);
        setPendingTowelsDays([]);
        return "Preferences reset. Remember to save your changes.";

      case 'EXIT_ATTEMPT':
        // Called when voice command tries to navigate away
        const exitAllowed = handleExitAttempt();
        return exitAllowed ? 'EXIT_ALLOWED' : 'EXIT_BLOCKED';

      default:
        return "Voice command not recognized.";
    }
  }, [pendingEcoEnabled, pendingSkipDays, pendingTowelsDays, hasUnsavedChanges, handleSave, handleDiscard, handleExitAttempt]);

  // Register voice command handler
  useEffect(() => {
    if (onVoiceCommandReady && isActive) {
      onVoiceCommandReady(handleVoiceCommand);
    }
  }, [onVoiceCommandReady, handleVoiceCommand, isActive]);

  // Prevent external navigation when there are unsaved changes
  useEffect(() => {
    if (!isActive || !hasUnsavedChanges) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges && !isExitingRef.current) {
        e.preventDefault();
        e.returnValue = '';
        console.log('[GreenStayView] Preventing page unload - unsaved changes');
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isActive, hasUnsavedChanges]);

  // Keyboard navigation
  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if modal is showing
      if (showExitWarning) return;

      // Don't trigger if user is typing in an input
      if ((e.target as HTMLElement).tagName === 'INPUT' || (e.target as HTMLElement).tagName === 'TEXTAREA') {
        return;
      }

      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          e.stopPropagation();
          handleExitAttempt();
          break;

        case 'Tab':
          e.preventDefault();
          e.stopPropagation();
          // Tab cycles through sections
          const sections: Array<'eco' | 'skip' | 'towels' | 'save' | 'discard'> = ['eco', 'skip', 'towels', 'save', 'discard'];
          const currentIndex = sections.indexOf(focusedSection);
          const nextIndex = (currentIndex + 1) % sections.length;
          const nextSection = sections[nextIndex];
          if (nextSection) setFocusedSection(nextSection);
          setFocusedDateIndex(0);
          break;

        case 'Enter':
          e.preventDefault();
          e.stopPropagation();
          if (focusedSection === 'eco') {
            toggleEcoMode();
          } else if (focusedSection === 'save') {
            if (hasUnsavedChanges) handleSave();
          } else if (focusedSection === 'discard') {
            if (hasUnsavedChanges) handleDiscard();
          } else if (pendingEcoEnabled && futureDates.length > 0) {
            if (focusedDateIndex === futureDates.length) {
              // "All" button
              if (focusedSection === 'skip') setAllSkipCleaning();
              else if (focusedSection === 'towels') setAllTowelsOnly();
            } else if (focusedDateIndex === futureDates.length + 1) {
              // "Clear" button
              if (focusedSection === 'skip') clearAllSkipCleaning();
              else if (focusedSection === 'towels') clearAllTowelsOnly();
            } else if (focusedDateIndex < futureDates.length) {
              // Date button
              const targetDate = futureDates[focusedDateIndex];
              if (targetDate) {
                if (focusedSection === 'skip') toggleSkipCleaningDay(targetDate.date, targetDate.isPast);
                else if (focusedSection === 'towels') toggleTowelsOnlyDay(targetDate.date, targetDate.isPast);
              }
            }
          }
          break;

        case 'ArrowRight':
          e.preventDefault();
          e.stopPropagation();
          if (focusedSection !== 'eco' && focusedSection !== 'save' && focusedSection !== 'discard' && futureDates.length > 0) {
            const maxIndex = futureDates.length + 1;
            setFocusedDateIndex((prev) => (prev + 1) % (maxIndex + 1));
          }
          break;

        case 'ArrowLeft':
          e.preventDefault();
          e.stopPropagation();
          if (focusedSection !== 'eco' && focusedSection !== 'save' && focusedSection !== 'discard' && futureDates.length > 0) {
            const maxIndex = futureDates.length + 1;
            setFocusedDateIndex((prev) => (prev - 1 + maxIndex + 1) % (maxIndex + 1));
          }
          break;

        case 'ArrowDown':
          e.preventDefault();
          e.stopPropagation();
          const sectionsDown: Array<'eco' | 'skip' | 'towels' | 'save' | 'discard'> = ['eco', 'skip', 'towels', 'save', 'discard'];
          const currentIndexDown = sectionsDown.indexOf(focusedSection);
          if (currentIndexDown < sectionsDown.length - 1) {
            const nextSection = sectionsDown[currentIndexDown + 1];
            if (nextSection) setFocusedSection(nextSection);
            setFocusedDateIndex(0);
          }
          break;

        case 'ArrowUp':
          e.preventDefault();
          e.stopPropagation();
          const sectionsUp: Array<'eco' | 'skip' | 'towels' | 'save' | 'discard'> = ['eco', 'skip', 'towels', 'save', 'discard'];
          const currentIndexUp = sectionsUp.indexOf(focusedSection);
          if (currentIndexUp > 0) {
            const prevSection = sectionsUp[currentIndexUp - 1];
            if (prevSection) setFocusedSection(prevSection);
            setFocusedDateIndex(0);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    isActive, showExitWarning, focusedSection, focusedDateIndex, futureDates,
    pendingEcoEnabled, hasUnsavedChanges,
    toggleEcoMode, toggleSkipCleaningDay, toggleTowelsOnlyDay,
    setAllSkipCleaning, clearAllSkipCleaning, setAllTowelsOnly, clearAllTowelsOnly,
    handleSave, handleDiscard, handleExitAttempt
  ]);

  // Modal keyboard navigation
  useEffect(() => {
    if (!showExitWarning) return;

    const handleModalKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Tab':
          e.preventDefault();
          setModalFocusedButton(prev => prev === 'save' ? 'discard' : 'save');
          break;
        case 'ArrowLeft':
          e.preventDefault();
          setModalFocusedButton('save');
          break;
        case 'ArrowRight':
          e.preventDefault();
          setModalFocusedButton('discard');
          break;
        case 'Enter':
          e.preventDefault();
          if (modalFocusedButton === 'save') {
            handleSaveAndExit();
          } else {
            handleDiscardAndExit();
          }
          break;
        case 'Escape':
          e.preventDefault();
          setShowExitWarning(false);
          break;
      }
    };

    window.addEventListener('keydown', handleModalKeyDown);
    return () => window.removeEventListener('keydown', handleModalKeyDown);
  }, [showExitWarning, modalFocusedButton, handleSaveAndExit, handleDiscardAndExit]);

  return (
    <div className="green-stay-view h-full flex flex-col bg-gradient-to-br from-zaxia-dark via-zaxia-dark-deep to-zaxia-dark overflow-hidden">
      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-6xl mx-auto px-6 py-4 space-y-4">

          {/* Savings Display */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-4 gap-3"
          >
            <div className="p-4 rounded-xl bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20">
              <div className="flex items-center gap-3 mb-2">
                <Droplets className="w-5 h-5 text-blue-400" />
                <span className="text-sm text-blue-300">Water Saved</span>
              </div>
              <p className="text-2xl font-bold text-white">{savingsInfo.water.toFixed(1)}<span className="text-lg text-white/60 ml-1">L</span></p>
              <p className="text-xs text-white/40 mt-1">{(savingsInfo.water / 50).toFixed(1)} showers worth</p>
            </div>

            <div className="p-4 rounded-xl bg-gradient-to-br from-green-500/10 to-green-600/5 border border-green-500/20">
              <div className="flex items-center gap-3 mb-2">
                <Wind className="w-5 h-5 text-green-400" />
                <span className="text-sm text-green-300">CO₂ Reduced</span>
              </div>
              <p className="text-2xl font-bold text-white">{savingsInfo.co2.toFixed(2)}<span className="text-lg text-white/60 ml-1">kg</span></p>
              <p className="text-xs text-white/40 mt-1">{(savingsInfo.co2 / 0.021).toFixed(1)} tree-days</p>
            </div>

            <div className="p-4 rounded-xl bg-gradient-to-br from-yellow-500/10 to-yellow-600/5 border border-yellow-500/20">
              <div className="flex items-center gap-3 mb-2">
                <Zap className="w-5 h-5 text-yellow-400" />
                <span className="text-sm text-yellow-300">Energy Saved</span>
              </div>
              <p className="text-2xl font-bold text-white">{savingsInfo.energy.toFixed(1)}<span className="text-lg text-white/60 ml-1">kWh</span></p>
              <p className="text-xs text-white/40 mt-1">{(savingsInfo.energy / 0.015).toFixed(0)} phone charges</p>
            </div>

            <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-500/20">
              <div className="flex items-center gap-3 mb-2">
                <Sparkles className="w-5 h-5 text-emerald-400" />
                <span className="text-sm text-emerald-300">Impact Score</span>
              </div>
              <p className="text-2xl font-bold text-white">{calculateImpactScore(pendingSkipDays, pendingTowelsDays, stayCheckIn, stayCheckOut)}</p>
              <p className="text-xs text-white/40 mt-1">eco-participation %</p>
            </div>
          </motion.div>

          {/* Cards Grid: Eco Mode, Skip Cleaning, Towels Only */}
          <div className="grid grid-cols-3 gap-3">
            {/* Eco Mode column: card + action buttons below */}
            <div className="flex flex-col gap-2">
            {/* Eco Mode Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className={clsx(
                "p-3 rounded-xl border transition-all",
                pendingEcoEnabled
                  ? "bg-green-900/20 border-green-500/30"
                  : "bg-white/5 border-white/10",
                focusedSection === 'eco' && "ring-2 ring-zaxia-primary shadow-lg shadow-zaxia-primary/20"
              )}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className={clsx("w-4 h-4", pendingEcoEnabled ? "text-green-400" : "text-white/40")} />
                  <h3 className="text-sm font-medium text-white">Eco Mode</h3>
                </div>
                <button
                  onClick={toggleEcoMode}
                  className={clsx(
                    "w-10 h-5 rounded-full transition-all relative",
                    pendingEcoEnabled ? "bg-green-500" : "bg-white/20"
                  )}
                >
                  <motion.div
                    className="absolute top-0.5 w-4 h-4 bg-white rounded-full shadow"
                    animate={{ left: pendingEcoEnabled ? '22px' : '2px' }}
                    transition={{ duration: 0.2 }}
                  />
                </button>
              </div>
            </motion.div>
            {/* Discard / Save — beneath Eco Mode only */}
            <div className="flex gap-2">
              <motion.button
                onClick={handleDiscard}
                disabled={!hasUnsavedChanges}
                className={clsx(
                  "flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs transition-all",
                  focusedSection === 'discard' && "ring-2 ring-zaxia-primary scale-105",
                  hasUnsavedChanges
                    ? "bg-white/5 hover:bg-white/10 text-white/80"
                    : "bg-white/5 text-white/20 cursor-not-allowed"
                )}
              >
                <RotateCcw className="w-3 h-3" />
                <span>Discard</span>
              </motion.button>
              <motion.button
                onClick={handleSave}
                disabled={!hasUnsavedChanges}
                className={clsx(
                  "flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-all",
                  focusedSection === 'save' && "ring-2 ring-zaxia-primary scale-105",
                  hasUnsavedChanges
                    ? "bg-gradient-to-r from-emerald-500 to-green-500 text-white"
                    : "bg-white/5 text-white/20 cursor-not-allowed"
                )}
              >
                <Save className="w-3 h-3" />
                <span>Save</span>
              </motion.button>
            </div>
            {/* Unsaved changes indicator — static, no layout shift */}
            <div className="h-5 flex items-center">
              {hasUnsavedChanges && (
                <span className="flex items-center gap-1 text-[10px] text-amber-400">
                  <AlertTriangle className="w-3 h-3" />
                  Unsaved changes
                </span>
              )}
            </div>
            </div>{/* end Eco Mode column */}

            {/* Skip Cleaning Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className={clsx(
                "p-3 rounded-xl border transition-all",
                pendingSkipDays.length > 0
                  ? "bg-blue-900/20 border-blue-500/30"
                  : "bg-white/5 border-white/10",
                !pendingEcoEnabled && "opacity-50",
                focusedSection === 'skip' && "ring-2 ring-zaxia-primary shadow-lg shadow-zaxia-primary/20"
              )}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="w-4 h-4 text-sm leading-none flex items-center justify-center flex-shrink-0">🧹</span>
                  <h3 className="text-sm font-medium text-white">Skip Cleaning</h3>
                </div>
                {pendingSkipDays.length > 0 && (
                  <span className="px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-300 text-[10px] font-medium">
                    {pendingSkipDays.length}
                  </span>
                )}
              </div>
              <p className="text-xs text-white/60 mb-2">
                Skip daily cleaning
              </p>

              {pendingEcoEnabled && stayDates.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] text-white/50">Select days:</span>
                    <div className="flex gap-2">
                      <button
                        onClick={setAllSkipCleaning}
                        className={clsx(
                          "text-[10px] px-2 py-0.5 rounded transition-all",
                          focusedSection === 'skip' && focusedDateIndex === futureDates.length
                            ? "bg-blue-500/30 text-blue-200 ring-2 ring-zaxia-primary scale-110"
                            : "text-blue-400 hover:text-blue-300"
                        )}
                      >
                        All
                      </button>
                      <button
                        onClick={clearAllSkipCleaning}
                        className={clsx(
                          "text-[10px] px-2 py-0.5 rounded transition-all",
                          focusedSection === 'skip' && focusedDateIndex === futureDates.length + 1
                            ? "bg-red-500/30 text-red-200 ring-2 ring-zaxia-primary scale-110"
                            : "text-red-400 hover:text-red-300"
                        )}
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {stayDates.map(({ date, isPast }) => {
                      const isSelected = pendingSkipDays.includes(date);
                      const dateObj = new Date(date + 'T00:00:00Z');
                      const dayNum = dateObj.getUTCDate();
                      const dayOfWeek = getDayOfWeek(date);
                      const futureIndex = futureDates.findIndex(d => d.date === date);
                      const isFocused = focusedSection === 'skip' && futureIndex === focusedDateIndex;

                      return (
                        <button
                          key={date}
                          onClick={() => toggleSkipCleaningDay(date, isPast)}
                          disabled={isPast}
                          className={clsx(
                            "flex flex-col items-center justify-center w-9 h-10 rounded text-[10px] transition-all",
                            isPast && "opacity-40 cursor-not-allowed",
                            isSelected
                              ? "bg-blue-500/40 text-blue-200 border border-blue-400/50"
                              : "bg-white/5 text-white/40 border border-white/10",
                            !isPast && !isSelected && "hover:bg-white/10",
                            isFocused && "ring-2 ring-zaxia-primary scale-110"
                          )}
                        >
                          <span className="text-[9px] opacity-70">{dayOfWeek}</span>
                          <span className="font-medium text-xs">{dayNum}</span>
                          {isSelected && <Check className="w-2.5 h-2.5" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </motion.div>

            {/* Towels Only Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className={clsx(
                "p-3 rounded-xl border transition-all",
                pendingTowelsDays.length > 0
                  ? "bg-purple-900/20 border-purple-500/30"
                  : "bg-white/5 border-white/10",
                !pendingEcoEnabled && "opacity-50",
                focusedSection === 'towels' && "ring-2 ring-zaxia-primary shadow-lg shadow-zaxia-primary/20"
              )}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Droplets className={clsx("w-4 h-4", pendingTowelsDays.length > 0 ? "text-purple-400" : "text-white/40")} />
                  <h3 className="text-sm font-medium text-white">Towels Only</h3>
                </div>
                {pendingTowelsDays.length > 0 && (
                  <span className="px-1.5 py-0.5 rounded-full bg-purple-500/20 text-purple-300 text-[10px] font-medium">
                    {pendingTowelsDays.length}
                  </span>
                )}
              </div>
              <p className="text-xs text-white/60 mb-2">
                Fresh towels only
              </p>

              {pendingEcoEnabled && stayDates.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] text-white/50">Select days:</span>
                    <div className="flex gap-2">
                      <button
                        onClick={setAllTowelsOnly}
                        className={clsx(
                          "text-[10px] px-2 py-0.5 rounded transition-all",
                          focusedSection === 'towels' && focusedDateIndex === futureDates.length
                            ? "bg-purple-500/30 text-purple-200 ring-2 ring-zaxia-primary scale-110"
                            : "text-purple-400 hover:text-purple-300"
                        )}
                      >
                        All
                      </button>
                      <button
                        onClick={clearAllTowelsOnly}
                        className={clsx(
                          "text-[10px] px-2 py-0.5 rounded transition-all",
                          focusedSection === 'towels' && focusedDateIndex === futureDates.length + 1
                            ? "bg-red-500/30 text-red-200 ring-2 ring-zaxia-primary scale-110"
                            : "text-red-400 hover:text-red-300"
                        )}
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {stayDates.map(({ date, isPast }) => {
                      const isSelected = pendingTowelsDays.includes(date);
                      const dateObj = new Date(date + 'T00:00:00Z');
                      const dayNum = dateObj.getUTCDate();
                      const dayOfWeek = getDayOfWeek(date);
                      const futureIndex = futureDates.findIndex(d => d.date === date);
                      const isFocused = focusedSection === 'towels' && futureIndex === focusedDateIndex;

                      return (
                        <button
                          key={date}
                          onClick={() => toggleTowelsOnlyDay(date, isPast)}
                          disabled={isPast}
                          className={clsx(
                            "flex flex-col items-center justify-center w-9 h-10 rounded text-[10px] transition-all",
                            isPast && "opacity-40 cursor-not-allowed",
                            isSelected
                              ? "bg-purple-500/40 text-purple-200 border border-purple-400/50"
                              : "bg-white/5 text-white/40 border border-white/10",
                            !isPast && !isSelected && "hover:bg-white/10",
                            isFocused && "ring-2 ring-zaxia-primary scale-110"
                          )}
                        >
                          <span className="text-[9px] opacity-70">{dayOfWeek}</span>
                          <span className="font-medium text-xs">{dayNum}</span>
                          {isSelected && <Check className="w-2.5 h-2.5" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="p-4 rounded-xl bg-white/5 border border-white/10">
              <p className="text-sm text-white/70 leading-relaxed">
                💡 Your eco-friendly choices help reduce water consumption, energy usage, and carbon emissions.
                Thank you for making a positive impact!
              </p>
            </div>
          </motion.div>

          {/* Spacer for FloatingVoiceBar */}
          <div className="mt-9 h-4" />
        </div>
      </div>

      {/* Exit Warning Modal - Use portal to render at document body level */}
      {createPortal(
        <AnimatePresence>
          {showExitWarning && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999]"
              onClick={() => setShowExitWarning(false)}
            >
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 16 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl border border-white/10 p-6 max-w-md w-full mx-4 shadow-2xl"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-xl bg-amber-500/20">
                    <AlertTriangle className="w-6 h-6 text-amber-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white">Unsaved Changes</h3>
                </div>

                <p className="text-white/70 mb-6">
                  You have unsaved changes to your Green Stay preferences.
                </p>

                <div className="flex gap-3">
                  <motion.button
                    onClick={handleSaveAndExit}
                    className={clsx(
                      "flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-green-500 text-white font-medium hover:from-emerald-600 hover:to-green-600 transition-all",
                      modalFocusedButton === 'save' && "ring-2 ring-white/50 ring-offset-2 ring-offset-slate-900"
                    )}
                  >
                    <Save className="w-5 h-5" />
                    Save
                  </motion.button>

                  <motion.button
                    onClick={handleDiscardAndExit}
                    className={clsx(
                      "flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-red-500/20 text-red-300 hover:bg-red-500/30 border border-red-500/30 transition-all",
                      modalFocusedButton === 'discard' && "ring-2 ring-white/50 ring-offset-2 ring-offset-slate-900"
                    )}
                  >
                    <X className="w-5 h-5" />
                    Discard
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}

export { GreenStayView };
export default GreenStayView;
